import { Icon } from "@iconify/react";
import { AppModal, Input, Textarea } from "@/shared/components/dialog";
import { EmptyState } from "@/shared/components/EmptyState";
import { Button } from "@/shared/components/button";
import { Select } from "@/shared/components/select";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { Skeleton } from "antd";
import { ThemedSegmented } from "@/shared/components/ThemedSegmented";
import type { ConditionStatus, HealthConcernInput } from "@/features/patient/records/api/dataPipelineApi";
import { CONDITION_STATUS_LABEL, ConditionStatusControl } from "./ConditionStatusControl";
import { SinceAndUpdated } from "./DiagnosisView";
import type { HealthConcernRecord } from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import { NotAvailable } from "@/shared/components/NotAvailable";
import { cn } from "@/shared/lib/utils";
import { useState, useEffect } from "react";
import { useRevealSearchTarget, type SearchFocus } from "@/shared/hooks/useSearchFocus";
import { LG_QUERY, useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { listCardClass } from "@/shared/lib/cardStyles";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { RequiredMark } from "@/shared/components/RequiredMark";
import { UnderlineTabs } from "@/shared/components/UnderlineTabs";

type HealthConcern = HealthConcernRecord;

interface HealthConcernsSectionProps {
  concerns: HealthConcernRecord[];
  /** The ones the patient added themselves: same shape as a provider's. */
  patientConcerns: HealthConcernRecord[];
  /** Adds a concern (no id) or changes one of the patient's own (id). Rejects with a message on failure. */
  onSaveConcern?: (input: Partial<HealthConcernInput>, id?: string) => Promise<void>;
  /** Marks a provider's concern active / inactive / resolved (the hospital's record isn't changed). */
  onSetStatus?: (id: string, status: ConditionStatus) => Promise<void>;
  /** False while viewing someone else's profile: only the patient can change a status. */
  canEditStatus?: boolean;
  isLoading?: boolean;
  /** A header search result to open here (useSearchFocus.ts). */
  focus?: SearchFocus;
}

// Small pink pill used for status / severity / urgency labels across this section.
const TAG = "inline-flex items-center rounded-[3px] bg-[#F17893] px-1.5 py-[3px] text-[9px] font-semibold leading-none text-white";

const categoryLabel: Record<HealthConcern["category"], string> = {
  symptom:       "Symptom",
  condition:     "Condition",
  preventive:    "Preventive",
  lifestyle:     "Lifestyle",
  mental_health: "Mental Health",
};

const statusLabel: Record<HealthConcern["status"], string> = CONDITION_STATUS_LABEL;

const severityLabel: Record<HealthConcern["severity"], string | null> = {
  mild:     "Mild",
  moderate: "Moderate",
  severe:   "Severe",
  unknown:  null,
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** A date the chart doesn't give (never filled in from the upload day). */
const NOT_IN_RECORD = "Not in record";

function parseDate(dateString: string): Date | null {
  if (!dateString) return null;
  const date = new Date(dateString.slice(0, 10) + "T00:00:00");
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Jun 15, 2024" */
function formatDate(dateString: string): string {
  const date = parseDate(dateString);
  if (!date) return "Not available";
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/** "Today" when the date is today, otherwise "Jun 15, 2024". */
function formatRelativeDay(dateString: string): string {
  const today = new Date().toLocaleDateString("en-CA");
  return dateString.slice(0, 10) === today ? "Today" : formatDate(dateString);
}

// ── Concern list card ──────────────────────────────────────────────────────
function ConcernCard({
  concern, isSelected, onClick,
}: { concern: HealthConcern; isSelected: boolean; onClick: () => void }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={cn(
        "!h-auto !w-full !whitespace-normal !rounded-lg !px-[14px] !py-3 !text-left !shadow-none",
        listCardClass(isSelected)
      )}
    >
      {/* w-full is load-bearing: antd's Button centers its content by default
          (same root cause diagnosed in DiagnosticsImagingView's sidebar rows)
          — without it, this row shrinks to its own content width and floats
          centered inside the button instead of stretching edge-to-edge. */}
      <div className="flex w-full items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-words text-[15px] font-bold leading-5 text-[#180047]">{concern.concern}</span>
            <span className={TAG}>{statusLabel[concern.status]}</span>
          </div>
          {concern.icdCode && <p className="mt-2 text-[13px] leading-4 text-[#6B7280]">ICD-10: {concern.icdCode}</p>}
        </div>
        {/* Both dates come from the chart, never from the upload day. */}
        <SinceAndUpdated since={concern.onsetDate} updated={concern.documentedDate} updatedLabel="Documented on" empty={NOT_IN_RECORD} />
      </div>
    </Button>
  );
}

// ── Right detail panel ─────────────────────────────────────────────────────
function DetailPanel({
  concern, onClose, className, onSetStatus, canEditStatus = true,
}: {
  concern: HealthConcern; onClose: () => void; className?: string;
  onSetStatus?: (id: string, status: ConditionStatus) => Promise<void>;
  canEditStatus?: boolean;
}) {
  const severity = severityLabel[concern.severity];
  const category = concern.icdCode
    ? `${categoryLabel[concern.category]} · ICD-10 ${concern.icdCode}`
    : categoryLabel[concern.category];
  // Chart dates only; "Last Updated" adds the day you marked its status, if later.
  const timeline = [
    concern.onsetDate ? { date: formatDate(concern.onsetDate), event: "Started", detail: "When it began, per the chart" } : null,
    concern.documentedDate ? { date: formatDate(concern.documentedDate), event: "Documented", detail: "Written in the chart" } : null,
    concern.lastUpdated && concern.lastUpdated !== concern.documentedDate
      ? { date: formatDate(concern.lastUpdated), event: "Last Updated", detail: "Most recent change to this record" } : null,
  ].filter((x): x is { date: string; event: string; detail: string } => x !== null);

  return (
    <div className={cn("rounded-xl border border-[#E5E7EB] bg-white p-[14px]", className)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-lg bg-[#F17893]">
            <Icon icon="lucide:heart-plus" className="h-9 w-9 text-white" />
          </div>
          <div className="pt-0.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <h2 className="text-base font-bold leading-5 text-[#180047]">{concern.concern}</h2>
              {severity && <span className={TAG}>{severity}</span>}
            </div>
            <p className="mt-2 text-[13px] leading-4 text-[#6B7280]">
              {concern.onsetDate ? `Since ${formatDate(concern.onsetDate)}`
                : concern.documentedDate ? `Documented on ${formatDate(concern.documentedDate)}`
                : "Dates not in record"}
            </p>
          </div>
        </div>
        <Button variant="link" onClick={onClose} className="!h-auto !shrink-0 !p-0 !text-[#180047] hover:!text-[#E4002B]">
          <Icon icon="lucide:x" className="h-6 w-6" />
        </Button>
      </div>

      <ConditionStatusControl
        className="mt-4"
        info={concern.statusInfo}
        canEdit={canEditStatus}
        onChange={onSetStatus ? (s) => onSetStatus(concern.id, s) : undefined}
      />

      {/* About */}
      <h3 className="mt-4 text-sm font-bold text-[#180047]">About this concern</h3>
      <p className="mt-4 max-w-[320px] text-xs leading-[21px] text-[#180047]">
        {concern.notes || <NotAvailable label="No clinical notes were included with this record." />}
      </p>

      {/* Info boxes */}
      <div className="mt-5 flex flex-wrap items-start gap-6">
        <div className="min-w-[104px] rounded-lg border border-[#E5E7EB] px-3 py-2.5 shadow-sm">
          <p className="text-[13px] leading-4 text-[#180047]">Category</p>
          <p className="mt-2.5 flex items-center gap-1.5 text-[10px] font-semibold text-[#180047]">
            <span className="h-1 w-1 shrink-0 rounded-full bg-[#180047]" />
            {category}
          </p>
        </div>
        <div className="min-w-[104px] rounded-lg border border-[#E5E7EB] px-3 py-2.5 shadow-sm">
          <p className="text-[13px] leading-4 text-[#180047]">Since</p>
          <p className="mt-2.5 flex items-center gap-1.5 text-[10px] font-semibold text-[#180047]">
            <span className="h-1 w-1 shrink-0 rounded-full bg-[#180047]" />
            {concern.onsetDate ? formatDate(concern.onsetDate) : NOT_IN_RECORD}
          </p>
        </div>
        <div className="min-w-[104px] rounded-lg border border-[#E5E7EB] px-3 py-2.5 shadow-sm" data-testid="concern-documented-on">
          <p className="text-[13px] leading-4 text-[#180047]">Documented on</p>
          <p className="mt-2.5 flex items-center gap-1.5 text-[10px] font-semibold text-[#180047]">
            <span className="h-1 w-1 shrink-0 rounded-full bg-[#180047]" />
            {concern.documentedDate ? formatDate(concern.documentedDate) : NOT_IN_RECORD}
          </p>
        </div>
        <div className="px-1 py-1">
          <p className="text-xs text-[#6B7280]">Last Update</p>
          <p className="mt-4 text-center text-xs font-bold text-[#180047]">{formatRelativeDay(concern.lastUpdated)}</p>
        </div>
      </div>

      {/* Timeline */}
      <h3 className="mt-5 text-sm font-bold text-[#180047]">Timeline</h3>
      <div className="relative mt-16 space-y-10">
        {/* Vertical line between the first and last dot (rows are h-12, dots centered). */}
        {timeline.length > 1 && <div className="absolute left-4 top-6 bottom-6 w-px bg-[#E5E7EB]" />}
        {timeline.map((item, index) => {
          const isLast = index === timeline.length - 1;
          return (
            <div key={index} className="relative flex h-12 items-center">
              <div className="flex w-8 shrink-0 justify-center">
                <span className={cn(
                  "rounded-full",
                  isLast
                    ? "h-2.5 w-2.5 bg-[#180047] ring-4 ring-[#E5E7EB]"
                    : "h-2 w-2 bg-[#E4002B] ring-4 ring-[#FDE2E7]"
                )} />
              </div>
              <p className="ml-12 w-[110px] shrink-0 text-[13px] font-medium text-[#4B5563]">{item.date}</p>
              <div className="min-w-0 flex-1 text-center">
                <p className="text-xs text-[#6B7280]">{item.event}</p>
                <p className="mt-2 text-xs text-[#E4002B]">{item.detail}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Care Plan */}
      <h3 className="mt-16 text-sm font-bold text-[#180047]">Care Plan</h3>
      <EmptyState
        className="mt-4 rounded-lg border-dashed py-8 shadow-none"
        icon={<Icon icon="lucide:clipboard-list" className="h-8 w-8 text-[#9CA3AF]" />}
        title="No care plan linked"
        description="See the Care & Treatment page for your care plans."
      />
    </div>
  );
}

// ── Patient-reported concern card ──────────────────────────────────────────
const EMPTY_FORM: HealthConcernInput = { name: "", status: "active", onsetDate: "", note: "" };

function PatientConcernCard({
  concern, onEdit, onSetStatus, busy, searchId,
}: {
  concern: HealthConcern; onEdit?: () => void;
  /** The concern's id, for the header search's "open this record" (useSearchFocus.ts). */
  searchId?: string;
  /** Their own concern: the status itself changes (no hospital record behind it). */
  onSetStatus?: (status: ConditionStatus) => Promise<void>;
  busy: boolean;
}) {
  return (
    <div data-testid="patient-concern" data-search-id={searchId} className="flex flex-col rounded-xl border border-[#E5E7EB] bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 flex-1 break-words text-base font-bold capitalize leading-snug text-[#180047]">{concern.concern}</h3>
        <span className={TAG}>{statusLabel[concern.status]}</span>
      </div>

      {/* The two dates as labelled columns, one format, one colour. */}
      {(concern.onsetDate || concern.reportedDate) && (
        <dl className="!mb-0 mt-5 grid grid-cols-2 gap-x-6">
          {concern.onsetDate && (
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">Started</dt>
              <dd className="!mb-0 mt-1.5 text-[13px] font-medium text-[#374151]">{formatDate(concern.onsetDate)}</dd>
            </div>
          )}
          {concern.reportedDate && (
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">Added</dt>
              <dd className="!mb-0 mt-1.5 text-[13px] font-medium text-[#374151]">{formatDate(concern.reportedDate)}</dd>
            </div>
          )}
        </dl>
      )}

      {concern.notes && <p className="!mb-0 mt-4 text-[13px] leading-6 text-[#6B7280]">{concern.notes}</p>}

      {(onEdit || onSetStatus) && (
        // Status on the left (Active / Inactive / Resolved), Edit on the right.
        <div className="mt-5 flex flex-wrap items-end justify-between gap-2.5 border-t border-[#F0F1F3] pt-4">
          {onSetStatus ? (
            <ConditionStatusControl info={concern.statusInfo} canEdit onChange={onSetStatus} />
          ) : <span />}
          {onEdit && (
            <Button variant="outline" onClick={onEdit} disabled={busy} className="!h-8 !rounded-lg !border-[#E5E7EB] !px-3 !text-xs !font-semibold !text-[#180047]">
              <Icon icon="lucide:pencil" className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export function HealthConcernsSection({
  concerns, patientConcerns, onSaveConcern, onSetStatus, canEditStatus = true, isLoading = false, focus,
}: HealthConcernsSectionProps) {
  const [activeTab, setActiveTab]             = useState<"provider" | "patient">("provider");
  const [filterKey, setFilterKey]             = useState<"all" | ConditionStatus>("all");
  const [pickedConcern, setSelectedConcern]   = useState<HealthConcern | null>(null);
  // Always today's copy of the picked concern, so a status change shows at once.
  const selectedConcern = pickedConcern ? (concerns.find((c) => c.id === pickedConcern.id) ?? pickedConcern) : null;
  const [showAddDialog, setShowAddDialog]     = useState(false);
  // Below lg there's no room for the side panel: a tapped concern opens in a modal.
  const wide = useMediaQuery(LG_QUERY);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const selectConcern = (c: HealthConcern) => {
    setSelectedConcern(c);
    if (!wide) setDetailModalOpen(true);
  };

  // The add/edit form: exactly the fields a provider's concern has.
  const [form, setForm] = useState<HealthConcernInput>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const today = new Date().toLocaleDateString("en-CA");
  // Every required field must be filled: name, start date (today or earlier), status.
  const formValid = form.name.trim() !== "" && form.onsetDate !== "" && form.onsetDate <= today && Boolean(form.status);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowAddDialog(true);
  };
  const openEdit = (c: HealthConcern) => {
    setEditingId(c.id);
    setForm({ name: c.concern, status: c.status, onsetDate: c.onsetDate, note: c.notes });
    setShowAddDialog(true);
  };
  const save = async (input: Partial<HealthConcernInput>, id?: string, done?: string) => {
    if (!onSaveConcern || saving) return;
    setSaving(true);
    try {
      await onSaveConcern(input, id);
      toastSuccess(done ?? (id ? "Your concern was updated" : "Your concern was added"));
      setShowAddDialog(false);
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const activeConcerns     = concerns.filter(c => c.status === "active");
  const inactiveConcerns   = concerns.filter(c => c.status === "inactive");
  const resolvedConcerns   = concerns.filter(c => c.status === "resolved");

  // Auto-select first concern on load
  useEffect(() => {
    if (concerns.length > 0 && !selectedConcern) {
      setSelectedConcern(concerns[0]);
    }
  }, [concerns]);

  // A header search result: a provider's concern (selected) or one of the patient's own.
  useRevealSearchTarget(focus, (id) => concerns.some((c) => c.id === id) || patientConcerns.some((c) => c.id === id), (id) => {
    const provider = concerns.find((c) => c.id === id);
    if (provider) {
      setActiveTab("provider");
      setFilterKey("all");
      selectConcern(provider);
    } else {
      setActiveTab("patient");
    }
  });

  const visibleConcerns =
    filterKey === "active"     ? activeConcerns :
    filterKey === "inactive"   ? inactiveConcerns :
    filterKey === "resolved"   ? resolvedConcerns :
    concerns;

  const groups = [
    { title: "Active Concerns",   items: visibleConcerns.filter(c => c.status === "active")     },
    { title: "Inactive Concerns", items: visibleConcerns.filter(c => c.status === "inactive")   },
    { title: "Resolved Concerns", items: visibleConcerns.filter(c => c.status === "resolved")   },
  ];

  if (isLoading) {
    return (
      <div className="min-h-full bg-white px-4 py-4 lg:px-6 lg:py-5">
        <div className="flex items-start gap-5">
          <div className="flex-1 space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-lg border border-[#E5E7EB] bg-white p-4">
                <Skeleton active paragraph={{ rows: 2 }} />
              </div>
            ))}
          </div>
          <div className="hidden flex-1 rounded-xl border border-[#E5E7EB] bg-white p-5 lg:block">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-white px-4 py-4 lg:px-6 lg:py-5">

      {/* ── Centered pill tab switcher ── */}
      <div className="mb-5 flex justify-center">
        <ThemedSegmented
          selectedBg="#180047"
          itemColor="#6B7280"
          trackPadding={5}
          value={activeTab}
          onChange={(v) => setActiveTab(v as "provider" | "patient")}
          options={[
            { value: "provider", label: <span className="flex items-center gap-2 px-1.5 py-1 text-sm font-semibold"><Icon icon="lucide:stethoscope" className="h-5 w-5" />From Providers</span> },
            { value: "patient", label: <span className="flex items-center gap-2 px-1.5 py-1 text-sm font-semibold"><Icon icon="lucide:user-round" className="h-4 w-4" />My Concerns</span> },
          ]}
          className="!rounded-full !border !border-[#180047] [&_.ant-segmented-item]:!rounded-full [&_.ant-segmented-thumb]:!rounded-full"
        />
      </div>


      {/* ── Provider tab ── */}
      {/* Nothing recorded yet: one empty card (same as My Concerns'), not an
          empty list beside an empty detail panel. */}
      {activeTab === "provider" && concerns.length === 0 && (
        <EmptyState
          icon={<Icon icon="lucide:stethoscope" className="h-12 w-12 text-[#9CA3AF]" />}
          title="No concerns from your providers yet"
          description="Health concerns your providers record will appear here."
        />
      )}
      {activeTab === "provider" && concerns.length > 0 && (
        <div className="flex items-start gap-5">

          {/* Left panel */}
          <div className="w-full min-w-0 flex-1">

            {/* Status filter: the platform's underline tabs. */}
            <UnderlineTabs
              label="Concern status"
              active={filterKey}
              onChange={setFilterKey}
              className="mb-6"
              tabs={[
                { id: "all",        label: "All Concerns", count: concerns.length           },
                { id: "active",     label: "Active",       count: activeConcerns.length     },
                { id: "inactive",   label: "Inactive",     count: inactiveConcerns.length   },
                { id: "resolved",   label: "Resolved",     count: resolvedConcerns.length   },
              ]}
            />

            {/* Concern list, grouped by status */}
            <div className="grid gap-6">
              {groups.map(group => group.items.length > 0 && (
                <div key={group.title}>
                  <h2 className="mb-4 text-[17px] font-bold text-[#180047]">{group.title}</h2>
                  <div className="grid gap-4">
                    {group.items.map(c => (
                      <div key={c.id} data-search-id={c.id}>
                        <ConcernCard
                          concern={c}
                          isSelected={selectedConcern?.id === c.id}
                          onClick={() => selectConcern(c)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {visibleConcerns.length === 0 && (
                <EmptyState
                  icon={<Icon icon="lucide:heart" className="h-12 w-12 text-[#9CA3AF]" />}
                  title="No concerns in this category"
                />
              )}
            </div>
          </div>

          {/* Below lg: the tapped concern's details in a modal (no side panel). */}
          {!wide && selectedConcern && (
            <AppModal
              open={detailModalOpen}
              onOpenChange={setDetailModalOpen}
              width={640}
              hideCloseButton
              // The panel is its own card: no extra modal padding around it.
              className="[&_.ant-modal-content]:!bg-transparent [&_.ant-modal-content]:!p-0 [&_.ant-modal-content]:!shadow-none"
            >
              <DetailPanel concern={selectedConcern} onClose={() => setDetailModalOpen(false)} onSetStatus={onSetStatus} canEditStatus={canEditStatus} />
            </AppModal>
          )}

          {/* Right detail panel. Sticky so it stays in view while scrolling a
              long concerns list, instead of scrolling away above the fold
              (matches the pattern in FhirExportView's "Export Summary"). */}
          <div className="sticky top-5 hidden flex-1 lg:block">
            {selectedConcern ? (
              <DetailPanel
                concern={selectedConcern}
                onClose={() => setSelectedConcern(null)}
                onSetStatus={onSetStatus}
                canEditStatus={canEditStatus}
                className="min-h-[calc(100vh-200px)]"
              />
            ) : (
              <div className="flex flex-col items-center justify-center rounded-xl border border-[#E5E7EB] bg-white p-8 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-[#F17893]">
                  <Icon icon="lucide:heart-plus" className="h-7 w-7 text-white" />
                </div>
                <p className="mt-3 text-sm font-semibold text-[#180047]">Select a concern</p>
                <p className="mt-1 text-xs text-[#9CA3AF]">Click any concern on the left to view details</p>
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── Patient tab ── */}
      {activeTab === "patient" && (
        <div>
          <div className="mb-5 flex items-start justify-between gap-3">
            <h2 className="text-[17px] font-bold text-[#180047]">My Reported Concerns</h2>
            <Button
              onClick={openAdd}
              disabled={!onSaveConcern}
              className="!mt-2 !h-10 !rounded-lg !border-0 !bg-[#E4002B]/50 !px-4 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60 hover:!text-white"
            >
              Add Concern
            </Button>
          </div>

          {patientConcerns.length === 0 ? (
            <EmptyState
              icon={<Icon icon="lucide:user" className="h-12 w-12 text-[#9CA3AF]" />}
              title="No concerns reported yet"
              description="Add a symptom or health concern you want to keep track of. It stays separate from what your providers recorded."
            />
          ) : (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {patientConcerns.map(c => (
                <PatientConcernCard
                  key={c.id}
                  searchId={c.id}
                  concern={c}
                  busy={saving}
                  onEdit={onSaveConcern ? () => openEdit(c) : undefined}
                  onSetStatus={onSaveConcern ? (status) => onSaveConcern({ status }, c.id) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Add / edit concern dialog: the same fields a provider's concern has ── */}
      <AppModal
        open={showAddDialog}
        onOpenChange={(open) => !saving && setShowAddDialog(open)}
        title={<span className="text-[#0A2947]">{editingId ? "Edit Health Concern" : "Report a Health Concern"}</span>}
        description={<span className="text-[#4B5563]">Keep track of a symptom or concern. It's saved as reported by you, separate from your providers' records.</span>}
        width={520}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={saving} onClick={() => setShowAddDialog(false)} className="!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-medium !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]">
              Cancel
            </Button>
            <Button
              onClick={() => void save(form, editingId ?? undefined)}
              disabled={!formValid}
              loading={saving}
              className="!h-auto !rounded-lg !border-0 !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60 hover:!text-white disabled:!opacity-50"
            >
              {editingId ? "Save Changes" : "Add Concern"}
            </Button>
          </div>
        }
      >
          <div className="flex items-start gap-2 rounded-xl border border-[#E4002B]/20 bg-[#FFF0F3] px-3 py-2.5">
            <Icon icon="lucide:alert-triangle" className="mt-0.5 h-4 w-4 shrink-0 text-[#E4002B]" />
            <p className="text-xs text-[#E4002B]">For urgent concerns, please contact your healthcare provider directly.</p>
          </div>
          <div className="space-y-4 py-2">
            <div>
              <label htmlFor="concern-name" className="mb-1.5 block text-sm font-medium text-[#0A2947]">What is your concern?<RequiredMark /></label>
              <Input id="concern-name" maxLength={200} className="!w-full !rounded-lg !border-[#E5E7EB] !px-3 !py-2 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none" placeholder="e.g., Recurring headaches" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">When did it start?<RequiredMark /></label>
                {/* The platform's calendar, opened centred on screen: future dates
                    are blocked, today can be picked. */}
                <CalendarDateField
                  value={form.onsetDate}
                  onChange={(v) => setForm({ ...form, onsetDate: v })}
                  placeholder="Select a date"
                />
                {form.onsetDate > today && <p className="mt-1 text-xs text-[#E4002B]">The start date can't be in the future.</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">Status<RequiredMark /></label>
                <Select
                  className="!w-full !text-sm !text-[#0A2947]"
                  value={form.status}
                  onChange={next => setForm({ ...form, status: next as HealthConcernInput["status"] })}
                  options={[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                    { value: "resolved", label: "Resolved" },
                  ]}
                />
              </div>
            </div>
            <div>
              <label htmlFor="concern-note" className="mb-1.5 block text-sm font-medium text-[#0A2947]">Describe it</label>
              <Textarea id="concern-note" maxLength={2000} className="!w-full !rounded-lg !border-[#E5E7EB] !px-3 !py-2 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none" placeholder="What are you experiencing?" rows={4} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
      </AppModal>

    </div>
  );
}
